import Button, { ButtonProps } from "@mui/material/Button";
import { ReactNode } from "react";
import {
  colorActionPrimary,
  colorActionPrimaryHover,
  colorActionPrimaryText,
  colorBorder,
  colorBorderHover,
  colorSurface,
  colorSurfaceHover,
  colorTextPrimary,
} from "src/design/tokens";
import { darkBlue, lightBlue } from "src/design/ColorPalette";

type CustomColor = "black" | "inverted";
type ButtonColor = Exclude<ButtonProps["color"], undefined> | CustomColor;

interface CustomButtonProps extends Omit<ButtonProps, "color"> {
  color?: ButtonColor;
  children?: ReactNode;
  disabledBg?: string;
  disabledColor?: string;
}

const customColorStyles: Record<CustomColor, object> = {
  black: {
    backgroundColor: colorActionPrimary,
    color: colorActionPrimaryText,
    "&:hover": { backgroundColor: colorActionPrimaryHover },
  },
  inverted: {
    backgroundColor: colorSurface,
    color: colorTextPrimary,
    border: `1px solid ${colorBorder}`,
    "&:hover": {
      backgroundColor: colorSurfaceHover,
      borderColor: colorBorderHover,
    },
  },
};

const defaultStyles = {
  backgroundColor: lightBlue,
  color: colorActionPrimaryText,
  "&:hover": { backgroundColor: darkBlue },
};

const isCustomColor = (color?: ButtonColor): color is CustomColor =>
  color === "black" || color === "inverted";

export const CustomButton = ({
  color,
  sx,
  children,
  disabledBg,
  disabledColor,
  ...rest
}: CustomButtonProps) => {
  const custom = isCustomColor(color);
  const colorStyles = custom ? customColorStyles[color] : {};
  const base = custom || color ? {} : defaultStyles;

  return (
    <Button
      color={custom ? undefined : (color as ButtonProps["color"])}
      sx={{
        textTransform: "none",
        fontWeight: "bold",
        width: "fit-content",
        ...base,
        ...colorStyles,
        ...(disabledBg || disabledColor
          ? {
              "&.Mui-disabled": {
                ...(disabledBg ? { backgroundColor: disabledBg } : {}),
                ...(disabledColor ? { color: disabledColor } : {}),
              },
            }
          : {}),
        ...(sx as object),
      }}
      {...rest}
    >
      {children}
    </Button>
  );
};
